import {
  accountLast4Digits,
  formatAccountLast4,
} from './accounts-display';
import type {
  AccountInventoryRow,
  AccountsGroupBy,
  AccountsInventoryGroup,
  AccountsSelectOption,
  AccountsStatusFilter,
  AccountsTypeFilter,
} from './accounts.model';
import {
  ACCOUNTS_STATUS_ALL,
  ACCOUNTS_STATUS_OPEN,
  ACCOUNTS_TYPE_ALL,
} from './accounts.model';

export function isAccountOpen(row: AccountInventoryRow): boolean {
  if (typeof row.isOpen === 'boolean') {
    return row.isOpen;
  }
  if (typeof row.isActive === 'boolean') {
    return row.isActive;
  }
  return true;
}

function normalizedText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function uniqueSorted(values: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = normalizedText(value);
    const key = trimmed.toLocaleLowerCase('en-US');
    if (!trimmed || seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(trimmed);
  }
  return out.sort((a, b) => a.localeCompare(b, 'en-US', { sensitivity: 'base' }));
}

export function statusFilterOptions(accounts: AccountInventoryRow[]): AccountsSelectOption[] {
  return [
    { label: 'Default (open only)', value: ACCOUNTS_STATUS_OPEN },
    { label: 'All', value: ACCOUNTS_STATUS_ALL },
    ...uniqueSorted(accounts.map((row) => row.status)).map((status) => ({
      label: status,
      value: status,
    })),
  ];
}

export function typeFilterOptions(accounts: AccountInventoryRow[]): AccountsSelectOption[] {
  return [
    { label: 'All', value: ACCOUNTS_TYPE_ALL },
    ...uniqueSorted(accounts.map((row) => row.type)).map((type) => ({
      label: type,
      value: type,
    })),
  ];
}

export const GROUP_BY_OPTIONS: AccountsSelectOption[] = [
  { label: 'None', value: 'none' },
  { label: 'Institution', value: 'institution' },
  { label: 'Type', value: 'type' },
  { label: 'Status', value: 'status' },
];

export function matchesAccountSearch(row: AccountInventoryRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  const last4 = (accountLast4Digits(row.idMasked) ?? '').toLowerCase();
  const last4Display = formatAccountLast4(row.idMasked).toLowerCase();
  return (
    normalizedText(row.name).toLowerCase().includes(q) ||
    normalizedText(row.institution).toLowerCase().includes(q) ||
    last4.includes(q) ||
    last4Display.includes(q) ||
    (row.idMasked ?? '').toLowerCase().includes(q)
  );
}

export function filterInventoryAccounts(
  accounts: AccountInventoryRow[],
  options: {
    search: string;
    status: AccountsStatusFilter;
    type: AccountsTypeFilter;
  }
): AccountInventoryRow[] {
  const status = options.status;
  const type = options.type;
  return accounts.filter((row) => {
    if (!matchesAccountSearch(row, options.search)) {
      return false;
    }
    if (status === ACCOUNTS_STATUS_OPEN) {
      if (!isAccountOpen(row)) {
        return false;
      }
    } else if (status !== ACCOUNTS_STATUS_ALL) {
      if (normalizedText(row.status).toLowerCase() !== status.toLowerCase()) {
        return false;
      }
    }
    if (type !== ACCOUNTS_TYPE_ALL && normalizedText(row.type).toLowerCase() !== type.toLowerCase()) {
      return false;
    }
    return true;
  });
}

export function groupInventoryAccounts(
  rows: AccountInventoryRow[],
  groupBy: AccountsGroupBy
): AccountsInventoryGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: '', count: rows.length, rows }];
  }
  const buckets = new Map<string, { key: string; rows: AccountInventoryRow[] }>();
  for (const row of rows) {
    const raw = String(row[groupBy] ?? '').trim();
    const key = raw || '—';
    const bucketId = key.toLocaleLowerCase('en-US');
    const bucket = buckets.get(bucketId);
    if (bucket) {
      bucket.rows.push(row);
    } else {
      buckets.set(bucketId, { key, rows: [row] });
    }
  }
  return [...buckets.values()]
    .sort((a, b) => a.key.localeCompare(b.key, 'en-US', { sensitivity: 'base' }))
    .map(({ key, rows: grouped }) => ({
      key,
      label: key.toUpperCase(),
      count: grouped.length,
      rows: grouped,
    }));
}

export function filtersAreDefault(
  search: string,
  status: AccountsStatusFilter,
  type: AccountsTypeFilter,
  groupBy: AccountsGroupBy
): boolean {
  return (
    search.trim() === '' &&
    status === ACCOUNTS_STATUS_OPEN &&
    type === ACCOUNTS_TYPE_ALL &&
    groupBy === 'none'
  );
}
